import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlaneUp,
  faXmark,
  faCheck,
  faTriangleExclamation,
  faCircleInfo,
  faTrashCan,
  faRotate,
  faWifi,
} from '@fortawesome/free-solid-svg-icons';
import { useOffline } from '../../offline/OfflineContext';
import './OfflinePanel.css';

function bytes(n) {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function stamp(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString([], {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function hoursLeft(expiresAt) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.round((expiresAt - Date.now()) / 3600000));
}

/**
 * The "Make my IMT portal accessible before the flight" surface.
 *
 * Three states: nothing armed, building, armed. Kept in one modal because
 * they are the same mental task at different moments, and splitting them
 * across screens would mean a user mid-build cannot see what they are getting.
 */
const OfflinePanel = ({ open, onClose }) => {
  const {
    supported, online, offline, pack, packLive, progress, error,
    limits, prepare, cancel, clear, isPreparing, forcedOffline, setForcedOffline,
    packIncomplete, failedSections, includesDocuments,
  } = useOffline();

  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (!open) setConfirmClear(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isPreparing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, isPreparing]);

  if (!open) return null;

  const truncatedCollections = (pack?.collections || []).filter(
    (c) => c.truncated || c.listTruncated
  );
  const withErrors = (pack?.collections || []).filter((c) => c.errorCount > 0);
  // `incomplete` / `failedCount` come from the context so this panel and the
  // dashboard strip give the same verdict — they disagreed once already.
  const failedCount = failedSections;
  const incomplete = packIncomplete;
  // The nature of the failure changes the advice we give, so surface throttling.
  const throttled = (pack?.collections || []).some(
    (c) => c.errorCount > 0 && c.throttled
  );

  // total === 0 means "we don't know how much work there is yet" (the plan
  // probe). Render an indeterminate bar rather than a real one pinned at 0%,
  // which for the ~12s the probe takes is indistinguishable from hung.
  const indeterminate = !!progress && !progress.total;
  const pct = progress && progress.total
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : 0;

  return (
    <div className="hms-offline-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !isPreparing) onClose();
    }}>
      <div className="hms-offline-panel" role="dialog" aria-modal="true" aria-label="Offline access">
        <header className="hms-offline-panel-head">
          <div className="hms-offline-panel-title">
            <FontAwesomeIcon icon={faPlaneUp} />
            <span>Offline access</span>
          </div>
          <button
            type="button"
            className="hms-offline-panel-close"
            onClick={onClose}
            disabled={isPreparing}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="hms-offline-panel-body">
          {!supported && (
            <div className="hms-offline-note hms-offline-note--warn">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <div>
                This browser can&rsquo;t store an offline pack. Offline access needs a
                modern browser on a secure (https) connection.
              </div>
            </div>
          )}

          {/* ---------------------------------------------------- preparing */}
          {isPreparing && progress && (
            <div className="hms-offline-progress">
              <div className="hms-offline-progress-phase">{progress.phase}</div>
              {progress.label && (
                <div className="hms-offline-progress-label">{progress.label}</div>
              )}
              <div className="hms-offline-progress-track">
                <div
                  className={`hms-offline-progress-fill ${indeterminate ? 'is-indeterminate' : ''}`}
                  style={indeterminate ? undefined : { width: `${pct}%` }}
                />
              </div>
              {!indeterminate && (
                <div className="hms-offline-progress-meta">
                  <span>{progress.done} of {progress.total}</span>
                  <span>{pct}%</span>
                </div>
              )}
              <p className="hms-offline-hint">
                Keep this tab open and stay connected until it finishes.
              </p>
              <button type="button" className="hms-offline-btn hms-offline-btn--ghost" onClick={cancel}>
                Cancel
              </button>
            </div>
          )}

          {/* -------------------------------------------------------- armed */}
          {!isPreparing && packLive && (
            <>
              {/* Never claim success when a material share of the pack is
                  missing. A green tick above a buried error list is how
                  someone boards a plane believing they have their deals. */}
              {incomplete ? (
                <div className="hms-offline-note hms-offline-note--warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    <strong>Your snapshot is incomplete.</strong>{' '}
                    {failedCount} of {pack.collections.length} sections
                    couldn&rsquo;t be saved
                    {throttled ? ' because IMT was rate-limiting the download' : ''}.
                    Everything else was saved at {stamp(pack.generatedAt)} &mdash;
                    use <strong>Refresh now</strong> to try the rest again.
                  </div>
                </div>
              ) : (
                <div className="hms-offline-note hms-offline-note--ok">
                  <FontAwesomeIcon icon={faCheck} />
                  <div>
                    <strong>You&rsquo;re ready to fly.</strong> Your snapshot was saved at{' '}
                    {stamp(pack.generatedAt)} and expires in about{' '}
                    {hoursLeft(pack.expiresAt)} hours.
                  </div>
                </div>
              )}

              <div className="hms-offline-stats">
                <div className="hms-offline-stat">
                  <div className="hms-offline-stat-value">{pack.responseCount}</div>
                  <div className="hms-offline-stat-label">screens of data</div>
                </div>
                <div className="hms-offline-stat">
                  <div className="hms-offline-stat-value">{pack.fileCount}</div>
                  <div className="hms-offline-stat-label">documents</div>
                </div>
                <div className="hms-offline-stat">
                  <div className="hms-offline-stat-value">{bytes(pack.sizeBytes)}</div>
                  <div className="hms-offline-stat-label">of saved data</div>
                </div>
              </div>

              <div className="hms-offline-collections">
                {pack.collections
                  .filter((c) => c.records > 0)
                  .sort((a, b) => b.records - a.records)
                  .map((c) => (
                    <div key={c.key} className="hms-offline-collection">
                      <span className="hms-offline-collection-label">{c.label}</span>
                      <span className="hms-offline-collection-count">
                        {c.records}
                        {(c.truncated || c.listTruncated) && (
                          <span className="hms-offline-collection-trunc" title="Only part of this was saved">
                            partial
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
              </div>

              {/* Truncation is surfaced, never silent — a pack that quietly
                  holds half your partners is a trap. */}
              {truncatedCollections.length > 0 && (
                <div className="hms-offline-note hms-offline-note--warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    <strong>Some lists were too long to save in full:</strong>{' '}
                    {truncatedCollections.map((c) => c.label).join(', ')}. You&rsquo;ll
                    see the most recent records offline, not the complete list.
                  </div>
                </div>
              )}

              {pack.filesSkipped > 0 && (
                <div className="hms-offline-note hms-offline-note--warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    {pack.filesSkipped} document{pack.filesSkipped === 1 ? '' : 's'} weren&rsquo;t
                    saved (the pack caps at {pack.fileCount + pack.filesSkipped > 0 ? pack.fileCount : 0} files
                    to keep the download reasonable).
                  </div>
                </div>
              )}

              {withErrors.length > 0 && (
                <div className="hms-offline-note hms-offline-note--warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    Some data couldn&rsquo;t be saved from:{' '}
                    {withErrors.map((c) => c.label).join(', ')}.
                  </div>
                </div>
              )}
            </>
          )}

          {/* ------------------------------------------------- not yet armed */}
          {!isPreparing && !packLive && supported && (
            <>
              <p className="hms-offline-lead">
                Save a read-only copy of your IMT data to this device so you can keep
                working through a flight or anywhere without a connection.
              </p>

              <ol className="hms-offline-steps">
                <li>
                  <span className="hms-offline-step-num">1</span>
                  <span className="hms-offline-step-body">
                    <strong>Now, while you still have signal</strong>
                    <span>
                      Tap the button below and wait for it to finish. Takes about a
                      minute.
                    </span>
                  </span>
                </li>
                <li>
                  <span className="hms-offline-step-num">2</span>
                  <span className="hms-offline-step-body">
                    <strong>Stay signed in</strong>
                    <span>
                      Don&rsquo;t log out before you fly. IMT can restore your existing
                      session offline, but it <em>cannot</em> sign you in without a
                      connection. Closing the tab or shutting the laptop is fine.
                    </span>
                  </span>
                </li>
                <li>
                  <span className="hms-offline-step-num">3</span>
                  <span className="hms-offline-step-body">
                    <strong>On the plane, just open IMT</strong>
                    <span>
                      It switches to offline automatically — no toggle to remember. An
                      orange bar shows the time your data was saved.
                    </span>
                  </span>
                </li>
                <li>
                  <span className="hms-offline-step-num">4</span>
                  <span className="hms-offline-step-body">
                    <strong>Look, don&rsquo;t touch</strong>
                    <span>
                      Everything is read-only. Anything you try to save is refused on
                      the spot &mdash; never half-sent, and never queued up to fire
                      when you reconnect. Changes still have to be made online.
                    </span>
                  </span>
                </li>
                <li>
                  <span className="hms-offline-step-num">5</span>
                  <span className="hms-offline-step-body">
                    <strong>Back on wifi, it clears itself</strong>
                    <span>
                      IMT returns to live data on its own, and the saved copy is wiped
                      after 48 hours.
                    </span>
                  </span>
                </li>
              </ol>

              {includesDocuments && (
                <div className="hms-offline-note hms-offline-note--warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    <strong>Document downloads are switched on, so this will take
                    a while.</strong> Scanned documents are large, and they are
                    fetched one at a time. Start it well before you need to leave,
                    and stay on a good connection until it finishes.
                  </div>
                </div>
              )}

              <div className="hms-offline-note hms-offline-note--info">
                <FontAwesomeIcon icon={faCircleInfo} />
                <div>
                  <strong>Nothing is ever sent back to IMT.</strong> Offline is
                  read-only: no edit is stored, queued or re-sent later, so there is
                  nothing to sync and nothing that can conflict with the live
                  database. The copy on this device is only ever read from, and only
                  ever contains data you already have access to.
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="hms-offline-note hms-offline-note--warn">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <div>{error}</div>
            </div>
          )}

          {/* --------------------------------------- what will not work --- */}
          {!isPreparing && limits && limits.length > 0 && (
            <details className="hms-offline-limits">
              <summary>What won&rsquo;t work offline</summary>
              <ul>
                {limits.map((l) => (
                  <li key={l.key}>
                    <strong>{l.label}</strong>
                    <span>{l.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {!isPreparing && (
          <footer className="hms-offline-panel-foot">
            {packLive ? (
              <>
                {confirmClear ? (
                  <div className="hms-offline-confirm">
                    <span>Delete the saved copy from this device?</span>
                    <button
                      type="button"
                      className="hms-offline-btn hms-offline-btn--danger"
                      onClick={async () => { await clear(); setConfirmClear(false); }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="hms-offline-btn hms-offline-btn--ghost"
                      onClick={() => setConfirmClear(false)}
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="hms-offline-btn hms-offline-btn--ghost"
                      onClick={() => setConfirmClear(true)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} /> Delete from this device
                    </button>
                    <button
                      type="button"
                      className="hms-offline-btn hms-offline-btn--primary"
                      onClick={prepare}
                      disabled={!online}
                      title={!online ? 'You need a connection to refresh' : undefined}
                    >
                      <FontAwesomeIcon icon={faRotate} /> Refresh now
                    </button>
                  </>
                )}
              </>
            ) : (
              <button
                type="button"
                className="hms-offline-btn hms-offline-btn--primary hms-offline-btn--wide"
                onClick={prepare}
                disabled={!supported || !online}
                title={!online ? 'You need a connection to prepare' : undefined}
              >
                <FontAwesomeIcon icon={faPlaneUp} /> Make IMT available offline
              </button>
            )}
          </footer>
        )}

        {/* Test affordance: lets someone verify offline mode at their desk
            without pulling the network cable. Only offered once a pack exists. */}
        {packLive && !isPreparing && (
          <div className="hms-offline-simulate">
            <label>
              <input
                type="checkbox"
                checked={forcedOffline}
                onChange={(e) => setForcedOffline(e.target.checked)}
              />
              <FontAwesomeIcon icon={faWifi} />
              <span>Simulate being offline {offline && !forcedOffline ? '(already offline)' : ''}</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflinePanel;
